import { Badge, type BadgeProps } from '@heidi/design-system/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { useTranslations, type TranslateFunction } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import type { EnrichedReference } from '../../contracts';
import { formatSpecialty, isGuidelineSource, isPartnerCitation } from '../../contracts';
import { getAuthorityTier } from './authorityTiers';
import { getSourceTypeLabel } from './sourceIconUtils';

type BadgeSize = NonNullable<BadgeProps['size']>;

function buildTooltipContentFromReference(
  reference: EnrichedReference,
  t: TranslateFunction
): string | undefined {
  const isPartnerSource = isPartnerCitation(reference.source_type);
  const showGuideline = !isPartnerSource && isGuidelineSource(reference.websiteType);

  const tooltipLines: string[] = [];

  if (!isPartnerSource && reference.websiteType?.[0]) {
    tooltipLines.push(getSourceTypeLabel(reference.websiteType[0]));
  }

  if (
    reference.content_type &&
    !(showGuideline && reference.content_type.toLowerCase().includes('guideline'))
  ) {
    tooltipLines.push(reference.content_type);
  }

  if (!isPartnerSource && reference.impact_factor !== undefined) {
    tooltipLines.push(
      t('chat.impactFactor', { value: reference.impact_factor.toFixed(1) })
    );
  }

  if (!isPartnerSource && reference.specialty?.[0]) {
    tooltipLines.push(formatSpecialty(reference.specialty[0]));
  }

  return tooltipLines.length ? tooltipLines.join(' · ') : undefined;
}

interface AuthorityTierBadgeProps {
  score: number | null | undefined;
  size?: Extract<BadgeSize, 'sm' | 'md' | 'xs'>;
  className?: string;
  /** Optional: overrides computed tooltip content. */
  tooltipContent?: string;
  /**
   * Optional: when provided (and `tooltipContent` is not), tooltip content will be derived
   * from reference metadata (website type, content type, impact factor, specialty).
   */
  reference?: EnrichedReference;
}

export const AuthorityTierBadge = ({
  score,
  size = 'sm',
  className,
  tooltipContent,
  reference,
}: AuthorityTierBadgeProps) => {
  const { t } = useTranslations();
  const tier = getAuthorityTier(score);
  if (!tier) return null;

  const resolvedTooltipContent =
    tooltipContent ??
    (reference ? buildTooltipContentFromReference(reference, t) : undefined);

  const badge = (
    <Badge
      color="neutral"
      size={size}
      data-testid="source-authority-tier"
      className={cn(tier.bgClass, tier.textClass, className)}
    >
      {t(tier.labelKey)}
    </Badge>
  );

  if (!resolvedTooltipContent) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{badge}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {resolvedTooltipContent}
      </TooltipContent>
    </Tooltip>
  );
};
